'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { X, Wallet, ArrowUp, Gift, ChevronDown, Check, Trophy, DollarSign, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DatePicker } from '../ui/date-picker';
import { cn } from '@/lib/utils';
import { z } from 'zod';

interface AddTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  bankrollId: string;
  currency: string;
}

type TransactionType = 'Dépôt' | 'Retrait' | 'Rakeback';
type FormatType = 'MTT' | 'Cash Game' | 'Spin & Go';

const transactionTypes: { value: TransactionType; icon: React.ReactNode; label: string }[] = [
  { value: 'Dépôt', icon: <Wallet className="w-5 h-5" />, label: 'Dépôt' },
  { value: 'Retrait', icon: <ArrowUp className="w-5 h-5" />, label: 'Retrait' },
  { value: 'Rakeback', icon: <Gift className="w-5 h-5" />, label: 'Rakeback' },
];

const formatTypes: { value: FormatType | null; icon: React.ReactNode; label: string }[] = [
  { value: null, icon: null, label: 'Aucun format' },
  { value: 'MTT', icon: <Trophy className="w-5 h-5" />, label: 'MTT' },
  { value: 'Cash Game', icon: <DollarSign className="w-5 h-5" />, label: 'Cash Game' },
  { value: 'Spin & Go', icon: <Zap className="w-5 h-5" />, label: 'Spin & Go' },
];

const transactionSchema = z.object({
  type: z.enum(['Dépôt', 'Retrait', 'Rakeback']),
  amount: z.string().min(1, 'Le montant est requis').refine(
    (val) => {
      const num = parseFloat(val);
      return !isNaN(num) && num > 0;
    },
    { message: 'Le montant doit être un nombre positif' }
  ),
  date: z.date({ message: 'La date est requise' }),
  format: z.enum(['MTT', 'Cash Game', 'Spin & Go']).optional().nullable(),
  notes: z.string().optional(),
});

export const AddTransactionModal: React.FC<AddTransactionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  bankrollId,
  currency,
}) => {
  const t = useTranslations('bankrolls.detail.transactions.addModal');
  const [transactionType, setTransactionType] = useState<TransactionType>('Dépôt');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [format, setFormat] = useState<FormatType | null>(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [isFormatDropdownOpen, setIsFormatDropdownOpen] = useState(false);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const formatDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setIsTypeDropdownOpen(false);
      }
      if (formatDropdownRef.current && !formatDropdownRef.current.contains(event.target as Node)) {
        setIsFormatDropdownOpen(false);
      }
    };

    if (isTypeDropdownOpen || isFormatDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTypeDropdownOpen, isFormatDropdownOpen]);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setTransactionType('Dépôt');
      setAmount('');
      setDate(new Date());
      setFormat(null);
      setNotes('');
      setError('');
    }
  }, [isOpen]);

  useEffect(() => {
    // Reset format when transaction type changes (if not Rakeback)
    if (transactionType !== 'Rakeback') {
      setFormat(null);
    }
  }, [transactionType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      // Validate form data
      const formData = {
        type: transactionType,
        amount,
        date: date!,
        format: transactionType === 'Rakeback' ? format : null,
        notes: notes || undefined,
      };

      transactionSchema.parse(formData);

      setIsSubmitting(true);

      const response = await fetch(`/api/bankrolls/${bankrollId}/transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: transactionType,
          amount: parseFloat(amount),
          date: date!.toISOString(),
          format: transactionType === 'Rakeback' ? format || undefined : undefined,
          notes: notes || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de l\'ajout de la transaction');
      }

      // Success
      onSuccess?.();
      onClose();
    } catch (err: any) {
      if (err instanceof z.ZodError) {
        setError(err.issues[0]?.message || 'Validation error');
      } else {
        setError(err.message || 'Une erreur est survenue');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedType = transactionTypes.find((t) => t.value === transactionType);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-50 w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('title')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('subtitle')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Transaction Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('type')}
            </label>
            <div ref={typeDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
                className={cn(
                  'w-full flex items-center justify-between px-4 py-3 rounded-lg border',
                  'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                  'text-gray-900 dark:text-white',
                  'hover:bg-gray-50 dark:hover:bg-gray-600',
                  'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="text-gold">
                    {selectedType?.icon}
                  </div>
                  <span className="font-medium">{selectedType?.label}</span>
                </div>
                <ChevronDown className={cn(
                  'w-5 h-5 text-gray-400 transition-transform',
                  isTypeDropdownOpen && 'rotate-180'
                )} />
              </button>

              {isTypeDropdownOpen && (
                <div className="absolute z-10 w-full mt-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden">
                  {transactionTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => {
                        setTransactionType(type.value);
                        setIsTypeDropdownOpen(false);
                        setError('');
                      }}
                      className={cn(
                        'w-full flex items-center justify-between px-4 py-3 text-left',
                        'hover:bg-gray-50 dark:hover:bg-gray-600',
                        'transition-colors',
                        transactionType === type.value && 'bg-gold/10'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          'text-gold',
                          transactionType !== type.value && 'text-gray-400'
                        )}>
                          {type.icon}
                        </div>
                        <span className={cn(
                          'font-medium',
                          transactionType === type.value
                            ? 'text-gray-900 dark:text-white'
                            : 'text-gray-700 dark:text-gray-300'
                        )}>
                          {type.label}
                        </span>
                      </div>
                      {transactionType === type.value && (
                        <Check className="w-5 h-5 text-gold" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Amount */}
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('amount', { currency })}
            </label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setError('');
              }}
              placeholder="100.00"
              required
              className="w-full"
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('date')}
            </label>
            <DatePicker
              value={date}
              onChange={(selectedDate) => {
                setDate(selectedDate);
                setError('');
              }}
              placeholder={t('datePlaceholder')}
              className="w-full"
            />
          </div>

          {/* Format (only for Rakeback) */}
          {transactionType === 'Rakeback' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('format')}
              </label>
              <div ref={formatDropdownRef} className="relative">
                <button
                  type="button"
                  onClick={() => setIsFormatDropdownOpen(!isFormatDropdownOpen)}
                  className={cn(
                    'w-full flex items-center justify-between px-4 py-3 rounded-lg border',
                    'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                    'text-gray-900 dark:text-white',
                    'hover:bg-gray-50 dark:hover:bg-gray-600',
                    'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100'
                  )}
                >
                  <div className="flex items-center gap-3">
                    {format ? (
                      <>
                        <div className="text-gold">
                          {formatTypes.find((f) => f.value === format)?.icon}
                        </div>
                        <span className="font-medium">
                          {formatTypes.find((f) => f.value === format)?.label}
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('formatPlaceholder')}
                      </span>
                    )}
                  </div>
                  <ChevronDown className={cn(
                    'w-5 h-5 text-gray-400 transition-transform',
                    isFormatDropdownOpen && 'rotate-180'
                  )} />
                </button>

                {isFormatDropdownOpen && (
                  <div className="absolute z-10 w-full mt-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden">
                    {formatTypes.map((formatOption) => (
                      <button
                        key={formatOption.value || 'none'}
                        type="button"
                        onClick={() => {
                          setFormat(formatOption.value);
                          setIsFormatDropdownOpen(false);
                          setError('');
                        }}
                        className={cn(
                          'w-full flex items-center justify-between px-4 py-3 text-left',
                          'hover:bg-gray-50 dark:hover:bg-gray-600',
                          'transition-colors',
                          format === formatOption.value && 'bg-gold/10'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          {formatOption.icon && (
                            <div className={cn(
                              'text-gold',
                              format !== formatOption.value && 'text-gray-400'
                            )}>
                              {formatOption.icon}
                            </div>
                          )}
                          <span className={cn(
                            'font-medium',
                            format === formatOption.value
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-700 dark:text-gray-300'
                          )}>
                            {formatOption.label}
                          </span>
                        </div>
                        {format === formatOption.value && (
                          <Check className="w-5 h-5 text-gold" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('notes')}
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('notesPlaceholder')}
              rows={3}
              className={cn(
                'w-full px-3 py-2 rounded-lg border',
                'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                'text-gray-900 dark:text-white',
                'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100',
                'resize-none'
              )}
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-[#1a2332] via-[#0f172a] to-[#0c1220] hover:opacity-90 text-white font-semibold"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('adding') : t('add')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

